import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Save, X } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { getRoles, createRole, updateRole, deleteRole } from "../../lib/api";
import { DEFAULT_EMPTY_ROLE, EMPTY_ROLES, PLH_ROLE_NAME, PLH_ROLE_LABEL, PLH_ROLE_COLOR, TOAST_SAVED, TOAST_SAVE_ERROR, TOAST_REMOVED, TOAST_REMOVE_ERROR, TOAST_ADDED, TOAST_UPDATED, TOAST_NO_PERMISSION, TOAST_REQUIRED, TOAST_LOAD_ERROR } from "../../lib/constants";

const empty = DEFAULT_EMPTY_ROLE;

export default function AdminRoles({ readOnly = false }) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [deleteUid, setDeleteUid] = useState(null);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await getRoles();
      setRoles(Array.isArray(data) ? data : []);
    } catch {
      toast.error(TOAST_LOAD_ERROR);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function startEdit(r) { setEditing(r.uid); setForm({ name: r.name, label: r.label || "", color: r.color || "#C8A97E" }); }
  function startNew() { setEditing("new"); setForm(empty); }
  function cancel() { setEditing(null); setForm(empty); }

  async function save() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!form.name.trim()) return toast.error(TOAST_REQUIRED);
    setSaving(true);
    try {
      if (editing === "new") {
        const created = await createRole(form);
        setRoles(prev => [created, ...prev]);
        toast.success(TOAST_ADDED);
      } else {
        const updated = await updateRole({ uid: editing, ...form });
        setRoles(prev => prev.map(r => r.uid === editing ? updated : r));
        toast.success(TOAST_UPDATED);
      }
      cancel();
    } catch (err) {
      toast.error(err.message || TOAST_SAVE_ERROR);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (readOnly) { toast.warning(TOAST_NO_PERMISSION); return; }
    if (!deleteUid) return;
    try {
      await deleteRole(deleteUid);
      setRoles(prev => prev.filter(r => r.uid !== deleteUid));
      setDeleteUid(null);
      toast.success(TOAST_REMOVED);
    } catch {
      toast.error(TOAST_REMOVE_ERROR);
    }
  }

  if (loading) return <div className="py-12 text-center text-warmgray"><Loader2 className="animate-spin mx-auto text-champagne" size={24} /></div>;

  return (
    <div data-id="admin-roles" className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-serif text-3xl text-softblack">Funções</h2>
          <p className="text-warmgray text-sm mt-1">Gerencie os perfis de acesso do sistema.</p>
        </div>
        {!readOnly && (
          <button onClick={startNew} className="btn-primary !px-6 !py-2 !text-[11px] flex items-center gap-2">
            <Plus size={14} /> Nova Função
          </button>
        )}
      </div>

      {/* Form */}
      {editing && (
        <div className="bg-white border border-fendi p-6 max-w-2xl shadow-sm">
          <div className="flex items-center justify-between mb-6">
            <p className="eyebrow text-champagne">{editing === "new" ? "Nova Função" : "Editar Função"}</p>
            <button onClick={cancel} className="text-warmgray hover:text-softblack"><X size={18} /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Nome (key)</span>
              <input className="editorial-field mt-2 font-mono text-sm" value={form.name} onChange={e => setForm({ ...form, name: e.target.value.toLowerCase().replace(/\s+/g, "_") })} placeholder={PLH_ROLE_NAME} />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Label</span>
              <input className="editorial-field mt-2" value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder={PLH_ROLE_LABEL} />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-warmgray">Cor</span>
              <div className="flex gap-2 mt-2">
                <input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="w-10 h-10 border border-fendi rounded cursor-pointer" />
                <input className="editorial-field flex-1 font-mono text-sm" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} placeholder={PLH_ROLE_COLOR} />
              </div>
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-fendi/20">
            <button onClick={cancel} className="btn-ghost !border-fendi/20 !py-2 !px-6 text-[11px] uppercase tracking-widest">Cancelar</button>
            {!readOnly && (
              <button onClick={save} disabled={saving} className="btn-primary !py-2 !px-6 !text-[11px] flex items-center gap-2">
                <Save size={12} /> {saving ? "A guardar..." : "Guardar"}
              </button>
            )}
          </div>
        </div>
      )}

      {/* List */}
      <div className="space-y-3">
        {roles.length === 0 && <div className="bg-white border border-fendi p-8 text-center text-warmgray text-sm">{EMPTY_ROLES}</div>}
        {roles.map(r => (
          <div key={r.uid} className="bg-white border border-fendi px-6 py-4 flex items-center justify-between group hover:border-champagne/40 transition-colors">
            <div className="flex items-center gap-4">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color || "#C8A97E" }} />
              <div>
                <p className="text-sm font-medium text-softblack">{r.label || r.name}</p>
                <p className="text-[11px] text-warmgray font-mono">{r.name}</p>
              </div>
            </div>
            {!readOnly && (
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity md:opacity-100">
                <button onClick={() => startEdit(r)} className="text-warmgray hover:text-softblack p-1"><Pencil size={14} /></button>
                <button onClick={() => setDeleteUid(r.uid)} className="text-warmgray hover:text-destructive p-1"><Trash2 size={14} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      <AlertDialog open={!!deleteUid} onOpenChange={() => setDeleteUid(null)}>
        <AlertDialogContent className="bg-white border border-fendi rounded-none">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-3xl text-softblack">Eliminar Função?</AlertDialogTitle>
            <AlertDialogDescription className="text-warmgray text-base font-light leading-relaxed">
              Esta ação é irreversível.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-10 gap-4">
            <AlertDialogCancel className="btn-ghost !border-fendi/20 !py-3 !px-8 text-[11px] uppercase tracking-widest">Cancelar</AlertDialogCancel>
            {!readOnly && (
              <AlertDialogAction onClick={remove} className="bg-destructive text-white hover:bg-destructive/90 !py-3 !px-8 text-[11px] uppercase tracking-widest rounded-none">
                Confirmar Eliminação
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
